import { Component, OnInit, signal, computed, inject } from '@angular/core';

import { PhotoPricesService } from './photo-prices.service';
import { Size } from '../orders/order.model';
import { SizePriceComponent } from './size-price.component/size-price.component';
import { PromotionComponent } from '../promotions/promotion/promotion.component';
import { zip } from 'rxjs';
import { Promotion } from '../promotions/promotion';
import { PromotionService } from '../promotions/promotion.service';

@Component({
  selector: 'app-photo-prices',
  imports: [SizePriceComponent, PromotionComponent],
  templateUrl: './photo-prices.component.html',
  styleUrl: './photo-prices.component.css'
})
export class PhotoPricesComponent implements OnInit {
  private readonly photoPricesService = inject(PhotoPricesService);
  private readonly promotionService = inject(PromotionService);
  
  readonly sizes = signal<Size[]>([]);
  readonly editableSizes = signal<Size[]>([]);
  readonly loading = signal(true);
  readonly isEditing = signal(false);
  readonly saving = signal(false);
  readonly showPromotion = signal(false);
  readonly selectedSize = signal<Size | null>(null);
  readonly promotions = signal<Promotion[]>([]);
  
  // Computed property for display sizes
  readonly displaySizes = computed(() => 
    this.isEditing() ? this.editableSizes() : this.sizes()
  );

  ngOnInit() {
    this.loading.set(true);
    
    zip([
      this.photoPricesService.getSizes(),
      this.promotionService.getActivePromotions()
    ]).subscribe({
      next: ([sizes, promotions]) => {
        console.log('Sizes loaded:', sizes);
        console.log('Promotions loaded:', promotions);
        this.sizes.set(sizes);
        this.promotions.set(promotions);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading data:', error);
        this.sizes.set([]);
        this.promotions.set([]);
        this.loading.set(false);
      }
    });
  }

  startEdit() {
    this.editableSizes.set(structuredClone(this.sizes()));
    this.isEditing.set(true);
  }

  cancelEdit() {
    this.editableSizes.set([]);
    this.isEditing.set(false);
  }

  addSize() {
    const newSize: Size = { width: 1, height: 1, price: 0, mostPopular: false };
    // use update to add a new size to the editable array
    this.editableSizes.update(sizes => [...sizes, newSize]);
  }

  async saveChanges(): Promise<void> {
    // Ignore repeated clicks while a save is in progress
    if (this.saving()) return;
    this.saving.set(true);

    try {
      // deep copy of editable sizes to save
      const sizesToSave = structuredClone(this.editableSizes());

      // if nothing changed compared to current sizes, skip saving
      if (this.areSizesEqual(this.sizes(), sizesToSave)) {
        this.isEditing.set(false);
        this.editableSizes.set([]);
        console.log('No changes detected — skipping save.');
        return;
      }

      // persist to Firestore (company and lastUpdated are added by the service)
      const id = await this.photoPricesService.createPhotoPrice({ sizes: sizesToSave });

      // update local UI state only after successful save
      this.sizes.set(structuredClone(sizesToSave));
      this.isEditing.set(false);
      this.editableSizes.set([]);

      console.log('Saved PhotoPrice', id, this.sizes());
    } catch (error) {
      console.error('Error saving changes:', error);
    } finally {
      this.saving.set(false);
    }
  }

  private areSizesEqual(a: Size[], b: Size[]): boolean {
    if (a === b) return true;
    if (a.length !== b.length) return false;

    for (let i = 0; i < a.length; i++) {
      const x = a[i];
      const y = b[i];

      if (
        x.width !== y.width ||
        x.height !== y.height ||
        x.price !== y.price ||
        (!!x.mostPopular) !== (!!y.mostPopular)
      ) {
        return false;
      }
    }

    return true;
  }

  // remove a size from the editableSizes array by index
  removeSize(index: number) {
    this.editableSizes.update(sizes => {
      if (index < 0 || index >= sizes.length) return sizes;
      const next = sizes.slice();
      next.splice(index, 1);
      return next;
    });
  }

  onSizeChange(index: number, updatedSize: Size) {
    this.editableSizes.update(sizes => {
      if (index < 0 || index >= sizes.length) return sizes;
      const next = sizes.slice();
      next[index] = updatedSize;
      return next;
    });
  }

  onPromotionClick(size: Size) {
    this.selectedSize.set(size);
    this.showPromotion.set(true);
  }

  onPromotionSaved(savedPromotion: Promotion) {
    this.closePromotion();
    
    // Update promotions list efficiently
    this.promotions.update(promotions => {
      const existingIndex = promotions.findIndex(p => 
        p.size.width === savedPromotion.size.width && 
        p.size.height === savedPromotion.size.height
      );
      
      if (existingIndex >= 0) {
        // Update existing promotion
        const updated = [...promotions];
        updated[existingIndex] = savedPromotion;
        return updated;
      } else {
        // Add new promotion
        return [...promotions, savedPromotion];
      }
    });
  }

  // Update photo-prices.component.ts to handle deletion
  onPromotionDeleted(promotionId: string) {
    this.closePromotion();
    
    // Remove the deleted promotion from the list
    this.promotions.update(promotions => 
      promotions.filter(p => p.id !== promotionId)
    );
  }

  closePromotion() {
    this.showPromotion.set(false);
    this.selectedSize.set(null);
  }

  getPromotionForSize(size: Size): Promotion | undefined {
    return this.promotions().find(p => 
      p.size.width === size.width && p.size.height === size.height
    );
  }

  getActivePromotionForSize(size: Size): Promotion | undefined {
    const today = new Date().toISOString().split('T')[0]; // Today as YYYY-MM-DD string
    
    return this.promotions().find(promotion => {
      const sizeMatches = promotion?.size?.width === size.width && 
                         promotion?.size?.height === size.height;
      
      if (!sizeMatches) return false;
      
      return today <= promotion.endDate;
    });
  }
}
